import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import Logo from './Logo';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  TextField,
  Paper,
  Fade,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  FitnessCenter as GymIcon,
  Restaurant as FoodIcon,
  StickyNote2 as NoteIcon,
  Bedtime as SleepIcon,
  AutoStories as BookIcon,
  FlightTakeoff as FlightIcon,
  BeachAccess as VacationIcon,
  CloudQueue as WeatherIcon,
  LocalDining as RestaurantIcon,
  Flight as FlightTrackerIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import useSessionTimeout from '../hooks/useSessionTimeout';

const NavigationBar = ({ 
  onSearch, 
  showNotifications = true,
  notificationCount = 0 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearTimeout: clearSessionTimeout } = useSessionTimeout(2);
  
  // Get current app from the URL path
  const currentApp = location.pathname.substring(1) || 'news';
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    { id: 1, text: "Hello! How can I help you today?", sender: 'ai', timestamp: new Date() }
  ]);
  const [showMCPCommands, setShowMCPCommands] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    productivity: true,
    lifestyle: false,
    travel: false,
  });

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      // Clear session timeout before logout
      clearSessionTimeout();
      handleProfileMenuClose();
      await logout();
      // Navigate to login page after successful logout
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      // Still navigate to login page even if logout fails
      navigate('/login', { replace: true });
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleToggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAppChange = (appId) => {
    navigate(`/${appId}`);
    if (isMobile) {
      handleMobileMenuClose();
    }
  };

  const handleChatToggle = () => {
    setChatOpen(!chatOpen);
  };

  const handleSendMessage = async () => {
    if (chatMessage.trim()) {
      const newMessage = {
        id: Date.now(),
        text: chatMessage,
        sender: 'user',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, newMessage]);
      const userMessage = chatMessage;
      setChatMessage('');
      
      // Check if it's an MCP command
      if (userMessage.startsWith('@mcp')) {
        // Check if user is authenticated
        if (!user?.uid) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            text: "Please log in to use MCP commands.",
            sender: 'ai',
            timestamp: new Date(),
            isError: true
          }]);
          return;
        }

        // Add loading message
        const loadingMessage = {
          id: Date.now() + 1,
          text: "Processing MCP command...",
          sender: 'ai',
          timestamp: new Date(),
          isLoading: true
        };
        setChatMessages(prev => [...prev, loadingMessage]);
        
        try {
          // Parse MCP command to remove @mcp prefix
          const parseMCPCommand = (message) => {
            const mcpMatch = message.match(/^@mcp\s+(.+)/i);
            if (!mcpMatch) return message;
            
            const command = mcpMatch[1].trim().toLowerCase();
            const parts = command.split(' ');
            
            // Convert to backend format
            switch (parts[0]) {
              case 'list':
                if (parts[1] === 'tasks') return 'list_tasks';
                if (parts[1] === 'notes') return 'list_notes';
                if (parts[1] === 'meals') return 'list_meals';
                if (parts[1] === 'books') return 'list_books';
                if (parts[1] === 'sleep') return 'list_sleep';
                if (parts[1] === 'gym') return 'list_gym';
                if (parts[1] === 'stocks') return 'list_stocks';
                break;
              case 'get':
                if (parts[1] === 'summary') return 'get_summary';
                break;
              case 'add':
                if (parts[1] === 'task') return `add_task ${parts.slice(2).join(' ')}`;
                if (parts[1] === 'note') return `add_note ${parts.slice(2).join(' ')}`;
                if (parts[1] === 'meal') return `add_meal ${parts.slice(2).join(' ')}`;
                break;
              case 'help':
                return 'help';
              default:
                return command;
            }
            return command;
          };

          const parsedCommand = parseMCPCommand(userMessage);
          
          // Call MCP API
          const response = await fetch(API_ENDPOINTS.MCP, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              command: parsedCommand,
              userId: user?.uid
            })
          });
          
          const data = await response.json();
          
          // Remove loading message and add MCP response
          setChatMessages(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            
            // Format MCP response based on the data structure
            let responseText = "MCP command executed successfully.";
            
            if (data.status === 'success') {
              if (data.tasks) {
                responseText = `Found ${data.tasks.length} tasks:\n${data.tasks.map(task => `• ${task.title || task.name || 'Untitled'}`).join('\n')}`;
              } else if (data.notes) {
                responseText = `Found ${data.notes.length} notes:\n${data.notes.map(note => `• ${note.title || note.content?.substring(0, 50) || 'Untitled'}`).join('\n')}`;
              } else if (data.meals) {
                responseText = `Found ${data.meals.length} meals:\n${data.meals.map(meal => `• ${meal.food || meal.name || 'Untitled'} (${meal.quantity || 'N/A'}g)`).join('\n')}`;
              } else if (data.books) {
                responseText = `Found ${data.books.length} books:\n${data.books.map(book => `• ${book.title || book.name || 'Untitled'}`).join('\n')}`;
              } else if (data.sleep) {
                responseText = `Found ${data.sleep.length} sleep records:\n${data.sleep.map(sleep => `• ${sleep.date || 'Unknown date'}: ${sleep.hours || 'N/A'} hours`).join('\n')}`;
              } else if (data.gym) {
                responseText = `Found ${data.gym.length} gym sessions:\n${data.gym.map(gym => `• ${gym.exercise || gym.name || 'Untitled'}`).join('\n')}`;
              } else if (data.stocks) {
                responseText = `Found ${data.stocks.length} stock entries:\n${data.stocks.map(stock => `• ${stock.symbol || stock.name || 'Untitled'}: $${stock.price || 'N/A'}`).join('\n')}`;
              } else if (data.summary) {
                responseText = data.summary;
              } else {
                responseText = "MCP command executed successfully.";
              }
            } else if (data.status === 'error') {
              responseText = data.message || "MCP command failed.";
            } else if (data.message) {
              responseText = data.message;
            } else if (data.response) {
              responseText = data.response;
            }
            
            return [...withoutLoading, {
              id: Date.now() + 2,
              text: responseText,
              sender: 'ai',
              timestamp: new Date(),
              isMCP: true
            }];
          });
        } catch (error) {
          console.error('MCP API error:', error);
          // Remove loading message and add error response
          setChatMessages(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              id: Date.now() + 2,
              text: "I'm sorry, I couldn't process that MCP command. Please try again later.",
              sender: 'ai',
              timestamp: new Date()
            }];
          });
        }
      } else {
        // Add loading message
        const loadingMessage = {
          id: Date.now() + 1,
          text: "AI is thinking...",
          sender: 'ai',
          timestamp: new Date(),
          isLoading: true
        };
        setChatMessages(prev => [...prev, loadingMessage]);
        
        try {
          // Call gpt-oss-120b API
          const response = await fetch(API_ENDPOINTS.AI_CHAT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: userMessage,
              conversationHistory: chatMessages.slice(-10).map(msg => ({
                type: msg.sender,
                content: msg.text
              }))
            })
          });
          
          const data = await response.json();
          
          // Remove loading message and add AI response
          setChatMessages(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              id: Date.now() + 2,
              text: data.response || "I'm sorry, I couldn't process that request.",
              sender: 'ai',
              timestamp: new Date()
            }];
          });
        } catch (error) {
          console.error('Chat API error:', error);
          // Remove loading message and add error response
          setChatMessages(prev => {
            const withoutLoading = prev.filter(msg => !msg.isLoading);
            return [...withoutLoading, {
              id: Date.now() + 2,
              text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
              sender: 'ai',
              timestamp: new Date()
            }];
          });
        }
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const appSections = [
    {
      section: 'productivity',
      title: 'Productivity',
      icon: <DashboardIcon />,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, description: 'Overview' },
        { id: 'stocks', label: 'Stocks', description: 'Market tracking' },
        { id: 'notes', label: 'Notes', icon: <NoteIcon />, description: 'Quick notes' },
        { id: 'tasks', label: 'Tasks', description: 'Task management' },
      ]
    },
    {
      section: 'lifestyle',
      title: 'Lifestyle',
      icon: <GymIcon />,
      items: [
        { id: 'gym', label: 'Fitness', icon: <GymIcon />, description: 'Fitness tracking' },
        { id: 'food', label: 'Food', icon: <FoodIcon />, description: 'Nutrition tracking' },
        { id: 'sleep', label: 'Sleep', icon: <SleepIcon />, description: 'Sleep analysis' },
        { id: 'books', label: 'Books', description: 'Reading list' },
      ]
    },
    {
      section: 'ai',
      title: 'AI & Tools',
      icon: <ChatIcon />,
      items: [
        { id: 'askgroq', label: 'AI Assistant', icon: <ChatIcon />, description: 'AI Assistant' },
      ]
    }
  ];

  const getButtonStyles = (isActive) => ({
    color: '#1e293b', // text-slate-800
    background: 'transparent',
    fontWeight: 400, // Normal weight like landing page
    minWidth: 'auto',
    px: 2, // Add horizontal padding for spacing
    py: 1, // Add vertical padding
    fontSize: '0.875rem', // text-sm = 14px
    borderRadius: 0,
    border: 'none',
    whiteSpace: 'nowrap',
    minHeight: 'auto',
    boxShadow: 'none',
    position: 'relative',
    textTransform: 'none',
    textDecoration: 'none',
    '&:hover': {
      background: 'transparent',
      color: '#475569', // hover:text-slate-600
      transform: 'none',
    },
    transition: 'color 0.2s ease',
  });

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <img 
            src="/logo-icon.png" 
            alt="Life Bricks" 
            style={{
              height: '1.5rem', // h-6 equivalent to match landing page
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '1.125rem', // text-lg equivalent
              letterSpacing: '0.01em',
            }}
          >
            Life Bricks
          </Typography>
        </Box>
        <IconButton onClick={handleMobileMenuClose} size="small" sx={{ color: '#6b7280' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Main Navigation Items */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', mb: 1, px: 1 }}>
          Main Navigation
        </Typography>
        <Button
          fullWidth
          variant="text"
          onClick={() => navigate('/home')}
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1.5,
            mb: 1,
            color: '#1e293b',
            '&:hover': {
              backgroundColor: '#f8fafc',
            }
          }}
        >
          Home
        </Button>
        <Button
          fullWidth
          variant={currentApp === 'dashboard' ? 'contained' : 'text'}
          onClick={() => handleAppChange('dashboard')}
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1.5,
            mb: 1,
            color: currentApp === 'dashboard' ? 'white' : '#1e293b',
            backgroundColor: currentApp === 'dashboard' ? '#1565C0' : 'transparent',
            '&:hover': {
              backgroundColor: currentApp === 'dashboard' ? '#1565C0' : '#f8fafc',
            }
          }}
        >
          Dashboard
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={() => {/* TODO: Add insights functionality */}}
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1.5,
            mb: 1,
            color: '#1e293b',
            '&:hover': {
              backgroundColor: '#f8fafc',
            }
          }}
        >
          Insights
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={() => navigate('/track')}
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1.5,
            mb: 1,
            color: '#1e293b',
            '&:hover': {
              backgroundColor: '#f8fafc',
            }
          }}
        >
          Track
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={() => navigate('/more')}
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1.5,
            mb: 1,
            color: '#1e293b',
            '&:hover': {
              backgroundColor: '#f8fafc',
            }
          }}
        >
          More
        </Button>
      </Box>

      {/* App Sections */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {appSections.map((section) => (
          <Box key={section.section}>
            <ListItemButton
              onClick={() => handleToggleSection(section.section)}
              sx={{ 
                px: 3, 
                py: 1.5,
                borderRadius: '6px',
                mx: 1,
                '&:hover': {
                  backgroundColor: '#f8fafc',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: '#1565C0' }}>
                {section.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography sx={{ fontWeight: 600, color: '#1f2937' }}>
                    {section.title}
                  </Typography>
                }
              />
              {expandedSections[section.section] ? <ExpandLess sx={{ color: '#6b7280' }} /> : <ExpandMore sx={{ color: '#6b7280' }} />}
            </ListItemButton>
            
            <Collapse in={expandedSections[section.section]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {section.items.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleAppChange(item.id)}
                      selected={currentApp === item.id}
                      sx={{
                        pl: 4,
                        py: 1.5,
                        mx: 1,
                        borderRadius: '6px',
                        '&.Mui-selected': {
                          backgroundColor: '#f8fafc',
                          color: '#1565C0',
                          border: '1px solid #e5e7eb',
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#f8fafc',
                        },
                      }}
                    >
                      {item.icon && (
                        <ListItemIcon sx={{ minWidth: 40, color: currentApp === item.id ? '#1565C0' : '#6b7280' }}>
                          {item.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={
                          <Typography sx={{ 
                            fontWeight: currentApp === item.id ? 600 : 500,
                            color: currentApp === item.id ? '#1565C0' : '#1f2937'
                          }}>
                            {item.label}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ 
                            fontSize: '0.75rem',
                            color: '#6b7280'
                          }}>
                            {item.description}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
            
            <Divider sx={{ my: 1 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          zIndex: 1300,
          position: 'relative',
        }}
      >
        <Toolbar sx={{ 
          minHeight: '64px !important', 
          px: 0, 
          position: 'relative',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          maxWidth: '1152px', // max-w-6xl equivalent
          margin: '0 auto',
          width: '100%'
        }}>
          {/* Left side - Logo and Mobile Menu */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexShrink: 0,
            px: 0 // Move logo all the way to the left edge
          }}>
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
                sx={{ mr: 1, color: '#6b7280' }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 2,
                position: 'relative',
                gap: 3, // Changed from 1.5 to 3 to match footer gap
              }}
            >
              <img 
                src="/logo-icon.png" 
                alt="Life Bricks" 
                style={{
                  height: '1.5rem', // h-6 equivalent to match footer
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 600, // Changed from 600 to match footer font-semibold
                  color: '#1e293b',
                  fontSize: '1.125rem', // text-lg equivalent
                  letterSpacing: '0.01em',
                }}
              >
                Life Bricks
              </Typography>
            </Box>
          </Box>

          {/* Center - Main Navigation (Desktop) */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: 3, // gap-6 equivalent (1.5rem)
              alignItems: 'center',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              overflow: 'auto',
              px: 1,
            }}>
              {/* Main Navigation Items */}
              <Button
                variant="text"
                onClick={() => navigate('/home')}
                sx={getButtonStyles(false)}
              >
                Home
              </Button>
              <Button
                variant={currentApp === 'dashboard' ? 'contained' : 'text'}
                onClick={() => handleAppChange('dashboard')}
                sx={getButtonStyles(currentApp === 'dashboard')}
              >
                Dashboard
              </Button>
              <Button
                variant="text"
                onClick={() => {/* TODO: Add insights functionality */}}
                sx={getButtonStyles(false)}
              >
                Insights
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/track')}
                sx={getButtonStyles(false)}
              >
                Track
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/more')}
                sx={getButtonStyles(false)}
              >
                More
              </Button>
            </Box>
          )}

          {/* Right side - Actions and User Menu */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexShrink: 0,
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            gap: 1
          }}>
            {/* Admin Panel Button - Only for admin user */}
            {user?.email === 'coderbean49@gmail.com' && (
              <Tooltip title="Admin Panel">
                <IconButton
                  size="small"
                  onClick={() => handleAppChange('admin')}
                  sx={{
                    color: '#dc2626', // red-600 for admin
                    background: '#fef2f2', // red-50 background
                    border: '1px solid #fecaca', // red-200 border
                    '&:hover': {
                      backgroundColor: '#fee2e2', // red-100 on hover
                      color: '#b91c1c', // red-700 on hover
                    },
                    transition: 'all 0.2s ease',
                    marginRight: 1,
                  }}
                >
                  <AccountCircleIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Chat Button */}
            <Tooltip title="AI Chat">
              <IconButton
                onClick={handleChatToggle}
                size="small"
                sx={{ 
                  color: '#1e293b', // text-slate-800
                  background: 'transparent',
                  '&:hover': {
                    background: 'transparent',
                    color: '#475569', // hover:text-slate-600
                  },
                  transition: 'color 0.2s ease',
                }}
              >
                <ChatIcon />
              </IconButton>
            </Tooltip>

            {/* Search Button */}
            <Tooltip title="Search">
              <IconButton
                onClick={onSearch}
                size="small"
                sx={{ 
                  color: '#1e293b', // text-slate-800
                  background: 'transparent',
                  '&:hover': {
                    background: 'transparent',
                    color: '#475569', // hover:text-slate-600
                  },
                  transition: 'color 0.2s ease',
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            {showNotifications && (
              <Tooltip title="Notifications">
                <IconButton
                  size="small"
                  sx={{ 
                    color: '#1e293b', // text-slate-800
                    background: 'transparent',
                    '&:hover': {
                      background: 'transparent',
                      color: '#475569', // hover:text-slate-600
                    },
                    transition: 'color 0.2s ease',
                  }}
                >
                  <Badge 
                    badgeContent={notificationCount} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        background: '#ef4444',
                        boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)',
                      }
                    }}
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* User Menu */}
            <IconButton
              size="small"
              onClick={handleProfileMenuOpen}
              sx={{ 
                p: 0.5,
                '&:hover': {
                  background: 'transparent',
                },
                transition: 'color 0.2s ease',
              }}
            >
              <Avatar
                src={user?.photoURL}
                alt={user?.displayName}
                sx={{ 
                  width: 32, 
                  height: 32,
                  border: '1px solid #e5e7eb',
                  '&:hover': {
                    borderColor: '#475569', // hover:text-slate-600
                  },
                  transition: 'border-color 0.2s ease',
                }}
              >
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            backgroundColor: 'transparent',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            mt: 1,
            backgroundColor: 'white',
          },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937' }}>
            {user?.displayName || 'User'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem 
          onClick={handleSignOut} 
          sx={{ 
            py: 1.5, 
            px: 3,
            '&:hover': {
              backgroundColor: '#f8fafc',
            }
          }}
        >
          <LogoutIcon sx={{ mr: 1.5, fontSize: '1.1rem', color: '#6b7280' }} />
          <Typography sx={{ color: '#1f2937', fontWeight: 500 }}>
            Log out
          </Typography>
        </MenuItem>
      </Menu>

      {/* Chat Interface */}
      <Fade in={chatOpen} timeout={300}>
        <Paper
          sx={{
            position: 'fixed',
            top: 64,
            right: 16,
            width: 450,
            height: 600,
            zIndex: 1400,
            display: chatOpen ? 'flex' : 'none',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#000000',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              AI Assistant
            </Typography>
            <IconButton
              onClick={handleChatToggle}
              size="small"
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chat Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              backgroundColor: '#f8fafc',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#e5e7eb',
                borderRadius: '2px',
              },
            }}
          >
            {chatMessages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '80%',
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: message.sender === 'user' ? '#1565C0' : (message.isMCP ? '#f3f4f6' : '#ffffff'),
                    color: message.sender === 'user' ? 'white' : (message.isMCP ? '#000000' : '#1f2937'),
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: message.sender === 'ai' ? (message.isMCP ? '1px solid #374151' : '1px solid #e5e7eb') : 'none',
                  }}
                >
                  <Typography variant="body2">
                    {message.text}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      opacity: 0.7,
                      fontSize: '0.7rem',
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* MCP Commands */}
          <Box sx={{ p: 1, borderTop: '1px solid #e5e7eb', backgroundColor: '#f3f4f6' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                mb: showMCPCommands ? 1 : 0
              }}
              onClick={() => setShowMCPCommands(!showMCPCommands)}
            >
              <Typography variant="subtitle2" sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.8rem' }}>
                MCP Commands:
              </Typography>
              <IconButton size="small" sx={{ p: 0.5 }}>
                {showMCPCommands ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            
            {showMCPCommands && (
              <>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {[
                    { cmd: '@mcp list tasks', desc: 'List all tasks' },
                    { cmd: '@mcp list notes', desc: 'List all notes' },
                    { cmd: '@mcp list meals', desc: 'List all meals' },
                    { cmd: '@mcp list books', desc: 'List all books' },
                    { cmd: '@mcp list sleep', desc: 'List sleep sessions' },
                    { cmd: '@mcp list gym', desc: 'List gym sessions' },
                    { cmd: '@mcp get summary', desc: 'Get data summary' },
                    { cmd: '@mcp add task "New task"', desc: 'Add new task' },
                    { cmd: '@mcp add note "New note"', desc: 'Add new note' },
                    { cmd: '@mcp add meal "Food name"', desc: 'Add new meal' },
                    { cmd: '@mcp help', desc: 'Show all commands' }
                  ].map((item, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => setChatMessage(item.cmd)}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '0.6rem',
                        py: 0.3,
                        px: 0.8,
                        minWidth: 'auto',
                        borderColor: '#000000',
                        color: '#000000',
                        '&:hover': {
                          borderColor: '#333333',
                          color: '#333333',
                          backgroundColor: '#f3f4f6',
                        }
                      }}
                      title={item.desc}
                    >
                      {item.cmd}
                    </Button>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  Click any command above to use it. Hover for description.
                </Typography>
              </>
            )}
          </Box>

          {/* Chat Input */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Type your message..."
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#000000',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#000000',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!chatMessage.trim()}
              sx={{
                backgroundColor: '#1565C0',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#0D47A1',
                },
                '&:disabled': {
                  backgroundColor: '#e5e7eb',
                  color: '#9ca3af',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default NavigationBar;
